// SkyTracker @skytracker/web — FilterPanel (left side, mobile full-screen).
import type { Category, ClientFilters } from '@skytracker/shared';
import { useFleet } from '../store/useFleet.js';

const CATS: Array<{ value: Category; label: string }> = [
  { value: 'commercial', label: 'COMMERCIAL' },
  { value: 'ga', label: 'GENERAL AV' },
  { value: 'heli', label: 'HELI' },
  { value: 'military', label: 'MILITARY' },
  { value: 'glider', label: 'GLIDER' },
  { value: 'balloon', label: 'BALLOON' },
  { value: 'sonde', label: 'SONDE' },
  { value: 'drone', label: 'DRONE' },
];

export function FilterPanel(): JSX.Element {
  const filters = useFleet((s) => s.filters);
  const setFilters = useFleet((s) => s.setFilters);
  const open = useFleet((s) => s.panelLeftOpen);
  const setOpen = useFleet((s) => s.setPanelLeftOpen);

  const toggleCat = (c: Category): void => {
    const set = new Set(filters.categories ?? []);
    if (set.has(c)) set.delete(c);
    else set.add(c);
    const next: ClientFilters = { ...filters, categories: set.size === 0 ? undefined : Array.from(set) };
    setFilters(next);
  };

  return (
    <aside className={`panel panel-left ${open ? 'open' : ''}`}>
      <div className="panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>FILTERS</span>
        <button className="btn secondary" onClick={() => setOpen(false)} aria-label="close" style={{ display: 'none' }}>×</button>
      </div>

      <div className="panel-section">
        <div className="filter-row">
          <label>CATEGORY</label>
          <div className="checkbox-grid">
            {CATS.map((c) => {
              const checked = !filters.categories || filters.categories.includes(c.value);
              return (
                <label key={c.value}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleCat(c.value)}
                  />
                  {c.label}
                </label>
              );
            })}
          </div>
        </div>

        <div className="filter-row">
          <label>ALTITUDE (ft)</label>
          <div className="range-row">
            <input
              type="number"
              min={0}
              max={60_000}
              step={500}
              value={filters.altitude_ft?.[0] ?? 0}
              onChange={(e) => setFilters({ ...filters, altitude_ft: [Number(e.target.value), filters.altitude_ft?.[1] ?? 60_000] })}
            />
            <span style={{ color: 'var(--spectre-text-dim)' }}>—</span>
            <input
              type="number"
              min={0}
              max={60_000}
              step={500}
              value={filters.altitude_ft?.[1] ?? 60_000}
              onChange={(e) => setFilters({ ...filters, altitude_ft: [filters.altitude_ft?.[0] ?? 0, Number(e.target.value)] })}
            />
          </div>
        </div>

        <div className="filter-row">
          <label>SPEED (kt)</label>
          <div className="range-row">
            <input
              type="number"
              min={0}
              max={700}
              step={10}
              value={filters.speed_kt?.[0] ?? 0}
              onChange={(e) => setFilters({ ...filters, speed_kt: [Number(e.target.value), filters.speed_kt?.[1] ?? 700] })}
            />
            <span style={{ color: 'var(--spectre-text-dim)' }}>—</span>
            <input
              type="number"
              min={0}
              max={700}
              step={10}
              value={filters.speed_kt?.[1] ?? 700}
              onChange={(e) => setFilters({ ...filters, speed_kt: [filters.speed_kt?.[0] ?? 0, Number(e.target.value)] })}
            />
          </div>
        </div>

        <div className="filter-row">
          <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={Boolean(filters.emergency_only)}
              onChange={(e) => setFilters({ ...filters, emergency_only: e.target.checked || undefined })}
            />
            EMERGENCY ONLY
          </label>
        </div>
      </div>

      {filters.categories?.includes('drone') && (
        <div className="panel-section" style={{ color: 'var(--spectre-text-dim)', fontFamily: 'var(--font-mono)', fontSize: 11, lineHeight: 1.4 }}>
          No drones in coverage. Drone Remote ID broadcasts are line-of-sight only — they cannot be aggregated globally. SkyTracker shows drones only when a local Remote ID receiver is attached.
        </div>
      )}
    </aside>
  );
}
